// DashboardPage (page patterns, BL-189): a page whose answer is a few figures and what they're made of
// (Home's Needs you, Performance, the month so far; Accounting's Overview, Gross profit, How the ads did).
// Top to bottom: the title block (PageHead) with its figures; then, when there's one, what leads the page
// the whole width (How the ads did's channels); then the work on the left, about 60 % of the width, and what it needs on the right, divided by a hairline, each column a stack of Sections; then,
// the whole width, what's too wide for a column (each day, the technicians); then one muted line that
// says what's counted. One column under 900, the right column after the left.
// Right under the figures their rule is the divider: the first section (the lead's, or each column's)
// draws no rule of its own. What the figures are for (Performance's dates) and why some can't be counted (an ad
// account's notices) sit between the title and the figures.
// A section says its own state the way ListPage's regions do (useListWait): loading draws skeleton rows,
// still loading after 10 s it says so, after 30 s it gives up into Couldn't load and Try again; empty is
// one muted sentence, failed one red sentence and Try again. A page whose whole body is one answer (the
// month so far, a technician's scorecard) says it once, with `waiting`, in place of its sections.
// Not for a list (ListPage), a record (RecordPage), Setup (SettingsPage) or the board. The pattern never
// draws what's in a section: that's a catalogued part (GoalPace, Aging, ApprovalList…). Reads only
// --dashpage-*; PageHead, Section, Link and Skeleton read their own.
import type { ReactNode } from 'react';
import { Link } from '../../components/Link/Link.tsx';
import { Skeleton, SkeletonGroup } from '../../components/Skeleton/Skeleton.tsx';
import { useListWait, type ListStatus } from '../ListPage/ListPage.tsx';
import { PageHead, type PageHeadProps } from '../PageHead/PageHead.tsx';
import { Section } from '../Section/Section.tsx';
import './DashboardPage.css';

/** One section of a dashboard: a Section that says its own state. */
export interface DashboardSection {
  /** Its name, under the rule ("Needs you"); its key too, unless `id` is given. */
  title: string;
  /** Its key, where two sections could share a name. */
  id?: string;
  /** How many there are, beside the name; shown once it's ready. */
  count?: number;
  /** What's in it, when it's ready: a catalogued part. */
  children?: ReactNode;
  /** Ready (the default), loading, empty or failed; a long loading turns slow, then timed out. */
  status?: ListStatus;
  /** What it shows, for the words the pattern says ("the month"); its title by default. */
  what?: string;
  /** Empty: what isn't there, in a sentence. */
  emptyText?: string;
  /** Failed: what didn't load. */
  failedText?: string;
  onRetry?: () => void;
  /** How many skeleton rows to draw while loading (3). */
  loadingRows?: number;
  /** How long a load waits before it says it's slow, and before it gives up (ListWaitTimes'). */
  slowAfter?: number;
  giveUpAfter?: number;
}

/** The whole body's one answer, still to come or failed: said once, in place of the sections. */
export interface DashboardWaiting {
  status: ListStatus;
  /** What's coming ("the huddle"). */
  what: string;
  failedText?: string;
  onRetry?: () => void;
  loadingRows?: number;
}

export interface DashboardPageProps {
  /** The title block, with its figures (at most five) and its actions (at most two). */
  head: PageHeadProps;
  /** Why some figures can't be counted, the whole width, between the title and the figures. */
  notice?: ReactNode;
  /** What the figures are for (the dates), between the title and the figures. */
  bar?: ReactNode;
  /** What leads the page, the whole width, between the title block and the columns (How the ads did's
   * channels); without it the columns sit right under the title block. */
  lead?: readonly DashboardSection[];
  /** The work, on the left (about 60 %); the whole width when there's no `side`. */
  main: readonly DashboardSection[];
  /** What the work needs, on the right behind a hairline; after `main` under 900. Given empty, the work
   * keeps its 60 % (Home for someone who sees one section). */
  side?: readonly DashboardSection[];
  /** Under the columns, the whole width: what's too wide for a column. */
  below?: readonly DashboardSection[];
  /** One muted line at the end: what the figures count. */
  foot?: ReactNode;
  /** The whole body's one answer, while it isn't ready. */
  waiting?: DashboardWaiting;
  /** Over the page: a FormSheet, a dialog, a toast. */
  layers?: ReactNode;
}

/** What a part says while it isn't ready: rows, then slow, then Couldn't load; empty; failed. */
function Say({
  status,
  what,
  emptyText,
  failedText,
  onRetry,
  rows = 3,
  slowAfter,
  giveUpAfter,
}: {
  status: Exclude<ListStatus, 'ready'>;
  what: string;
  emptyText?: string | undefined;
  failedText?: string | undefined;
  onRetry?: (() => void) | undefined;
  rows?: number | undefined;
  slowAfter?: number | undefined;
  giveUpAfter?: number | undefined;
}) {
  const { wait, again } = useListWait(status === 'loading', slowAfter, giveUpAfter);
  const now = status === 'loading' ? wait : status;
  const skeleton = (label: string) => (
    <SkeletonGroup label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
  if (now === 'loading') return skeleton(`Loading ${what}`);
  if (now === 'slow')
    return (
      <>
        {skeleton(`Still loading ${what}`)}
        <p className="fl-dashpage__say" data-slow="" role="status">
          {`Still loading ${what}…`}
        </p>
      </>
    );
  if (now === 'timedout')
    return (
      <p className="fl-dashpage__say" data-bad="" role="alert">
        <span>{`Couldn’t load ${what}.`}</span>
        <Link
          onPress={() => {
            again();
            onRetry?.();
          }}
        >
          Try again
        </Link>
      </p>
    );
  if (now === 'empty') return <p className="fl-dashpage__say">{emptyText ?? 'Nothing here yet.'}</p>;
  return (
    <p className="fl-dashpage__say" data-bad="" role="alert">
      <span>{failedText ?? 'This didn’t load.'}</span>
      {onRetry ? <Link onPress={onRetry}>Try again</Link> : null}
    </p>
  );
}

function Part({ s }: { s: DashboardSection }) {
  const status = s.status ?? 'ready';
  return (
    <Section title={s.title} {...(status === 'ready' && s.count != null ? { count: s.count } : {})}>
      {status === 'ready' ? (
        s.children
      ) : (
        <Say
          status={status}
          what={s.what ?? s.title.toLowerCase()}
          emptyText={s.emptyText}
          failedText={s.failedText}
          onRetry={s.onRetry}
          rows={s.loadingRows}
          slowAfter={s.slowAfter}
          giveUpAfter={s.giveUpAfter}
        />
      )}
    </Section>
  );
}

const parts = (list: readonly DashboardSection[]) => list.map((s) => <Part key={s.id ?? s.title} s={s} />);

export function DashboardPage({
  head,
  notice,
  bar,
  lead,
  main,
  side,
  below,
  foot,
  waiting,
  layers,
}: DashboardPageProps) {
  // with something between the title and the figures, the pattern draws the strip itself, under it
  const between = !!(notice || bar);
  const { figures, ...title } = head;
  const body =
    waiting && waiting.status !== 'ready' ? (
      <div className="fl-dashpage__wait">
        <Say
          status={waiting.status}
          what={waiting.what}
          failedText={waiting.failedText}
          onRetry={waiting.onRetry}
          rows={waiting.loadingRows}
        />
      </div>
    ) : (
      <>
        {lead?.length ? <div className="fl-dashpage__lead">{parts(lead)}</div> : null}
        <div className="fl-dashpage__cols" data-side={side ? '' : undefined}>
          <div className="fl-dashpage__col">{parts(main)}</div>
          {side ? <div className="fl-dashpage__col">{parts(side)}</div> : null}
        </div>
        {below?.length ? <div className="fl-dashpage__below">{parts(below)}</div> : null}
      </>
    );
  return (
    <div
      className="fl-dashpage"
      data-figures={figures ? '' : undefined}
      data-lead={lead?.length ? '' : undefined}
    >
      <PageHead {...title} {...(figures && !between ? { figures } : {})} />
      {notice ? <div className="fl-dashpage__notice">{notice}</div> : null}
      {bar ? <div className="fl-dashpage__bar">{bar}</div> : null}
      {figures && between ? <div className="fl-dashpage__figs">{figures}</div> : null}
      {body}
      {foot ? <p className="fl-dashpage__foot">{foot}</p> : null}
      {layers}
    </div>
  );
}
